import React from 'react';

import { TwoFactorSettings } from '@/features/2fa/TwoFactorSettings';
import { CheckedState } from '@radix-ui/react-checkbox';
import { createFileRoute } from '@tanstack/react-router';
import { Bell, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { useIsOnlyMaintenanceReviewProduct } from '@/hooks/useIsOnlyMaintenanceReviewProduct';
import { useSettings } from '@/hooks/useSettings';

import { LoadingScreen } from '@/components/loading-screen';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/settings')({
    component: SettingsPage,
});

function SettingsPage() {
    const { settings, updateSettings } = useSettings();
    const {
        isOnlyMaintenanceReviewProduct,
        isLoading: isLoadingMaintenanceReviewProduct,
    } = useIsOnlyMaintenanceReviewProduct();

    const [localSettings, setLocalSettings] = React.useState(settings);

    React.useEffect(() => {
        setLocalSettings(settings);
    }, [settings]);

    const handleDebugChange = (checked: CheckedState) => {
        setLocalSettings((prev) => ({
            ...prev,
            isDebug: checked.valueOf() as boolean,
        }));
    };

    const handleSave = () => {
        updateSettings(localSettings);
        toast.success('Settings saved successfully');
    };

    if (isLoadingMaintenanceReviewProduct) return <LoadingScreen />;

    return (
        <div className="flex-1 min-h-full bg-muted/50" data-testid="Settings">
            <div className="p-8 pb-4 flex flex-col gap-4 bg-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Settings
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Manage your account, preferences, and team
                            configuration.
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <div className="mx-auto max-w-3xl space-y-6">
                    {/* Account & Security */}
                    <Card className="gap-0 py-0 overflow-hidden">
                        <CardHeader className="bg-muted/40 border-b px-4 py-3 flex-row items-center space-y-0 gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <CardTitle className="text-sm font-semibold">
                                Two-Factor Authentication
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="px-4 py-4">
                            <TwoFactorSettings />
                        </CardContent>
                    </Card>

                    {/* General Preferences */}
                    {!isOnlyMaintenanceReviewProduct && (
                        <Card className="gap-0 py-0 overflow-hidden">
                            <CardHeader className="bg-muted/40 border-b px-4 py-3 flex-row items-center space-y-0 gap-2">
                                <Bell className="h-4 w-4 text-muted-foreground" />
                                <CardTitle className="text-sm font-semibold">
                                    General Preferences
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 py-4 space-y-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-0.5">
                                        <Label
                                            htmlFor="debugMode"
                                            className="text-sm font-medium"
                                        >
                                            Debug Mode
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Enable detailed logging and
                                            debugging information.
                                        </p>
                                    </div>
                                    <Checkbox
                                        checked={localSettings.isDebug}
                                        onCheckedChange={handleDebugChange}
                                        id="debugMode"
                                    />
                                </div>

                                <div className="flex justify-end pt-2 border-t">
                                    <Button
                                        onClick={handleSave}
                                        size="sm"
                                        className="h-7 text-xs mt-2"
                                    >
                                        Save Preferences
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
